import React from 'react';
import { Input } from 'antd';
import { Alert, Button, Checkbox } from '~/ui/components';
import { Eye, InfoCircle, Lock, Megaphone, User } from '~/ui/assets/icons';
import CommentTypeBadge from '~/components/CommentTypeBadge/CommentTypeBadge';
import { PUBLIC_MESSAGE_MAX_LENGTH, RESERVED_MESSAGE_MAX_LENGTH } from '../constants';
import {
  StepBody,
  HeaderCard,
  HeaderLeft,
  PublicIconChip,
  ReservedIconChip,
  HeaderInfo,
  HeaderTitle,
  HeaderSubtitle,
  FieldGroup,
  CharCounter,
  HintBox,
  ConfirmBox,
  Footer,
  FooterHint,
} from '../RaFlow.styles';

const PUBLIC_WARNING =
  'Esta resposta ficará pública no Reclame Aqui, visível para qualquer pessoa. Use um tom institucional e não inclua dados pessoais, como CPF, número de contrato ou valores.';

type ReclameAquiStepProps = {
  publicMessage: string;
  reservedMessage: string;
  confirmed: boolean;
  publicRequired: boolean;
  reservedRequired: boolean;
  onPublicChange: (value: string) => void;
  onReservedChange: (value: string) => void;
  onConfirmChange: (value: boolean) => void;
  onReview: () => void;
};

function ReclameAquiStep({
  publicMessage,
  reservedMessage,
  confirmed,
  publicRequired,
  reservedRequired,
  onPublicChange,
  onReservedChange,
  onConfirmChange,
  onReview,
}: ReclameAquiStepProps) {
  const publicAtLimit = publicMessage.length >= PUBLIC_MESSAGE_MAX_LENGTH;
  const reservedAtLimit = reservedMessage.length >= RESERVED_MESSAGE_MAX_LENGTH;

  const publicFilled = !!publicMessage.trim();

  // Required messages depend on the target status: Concluído requires the public response,
  // Pendente Cliente requires the reserved message. Whenever a public response is written,
  // its publication must be confirmed.
  const canReview =
    (!publicRequired || publicFilled) &&
    (!reservedRequired || !!reservedMessage.trim()) &&
    (!publicFilled || confirmed);

  return (
    <StepBody>
      <HeaderCard accent="public">
        <HeaderLeft>
          <PublicIconChip>
            <Megaphone />
          </PublicIconChip>
          <HeaderInfo>
            <HeaderTitle accent="public">Resposta pública</HeaderTitle>
            <HeaderSubtitle>Publicada na página do Reclame Aqui</HeaderSubtitle>
          </HeaderInfo>
        </HeaderLeft>
        <CommentTypeBadge type="PUBLIC_RA" label="Visível a todos" icon={<Eye />} />
      </HeaderCard>

      <Alert status="warning" icon="warning" label={PUBLIC_WARNING} fullWidth />

      <HintBox>
        <InfoCircle />
        <span>Não inclua CPF, contrato ou valores — qualquer pessoa pode ler.</span>
      </HintBox>

      <FieldGroup>
        <Input.TextArea
          value={publicMessage}
          onChange={(e) => onPublicChange(e.target.value)}
          placeholder="Escreva a resposta que será publicada na página do Reclame Aqui..."
          autoSize={{ minRows: 4, maxRows: 8 }}
          maxLength={PUBLIC_MESSAGE_MAX_LENGTH}
          data-testid="ra-public-input"
        />
        <CharCounter error={publicAtLimit}>
          {publicMessage.length} / {PUBLIC_MESSAGE_MAX_LENGTH}
        </CharCounter>
      </FieldGroup>

      <ConfirmBox checked={publicFilled && confirmed} disabled={!publicFilled}>
        <Checkbox
          name="ra-confirm-public"
          checked={publicFilled && confirmed}
          onChange={onConfirmChange}
          disabled={!publicFilled}
          colorPalette={confirmed ? 'success' : 'primary'}
          data-testid="ra-confirm-checkbox"
        />
        <span>
          Confirmo a publicação desta resposta no Reclame Aqui. Ela ficará <b>pública e visível para qualquer pessoa</b>.
        </span>
      </ConfirmBox>

      <HeaderCard accent="reserved">
        <HeaderLeft>
          <ReservedIconChip>
            <Lock />
          </ReservedIconChip>
          <HeaderInfo>
            <HeaderTitle accent="reserved">Mensagem Privada</HeaderTitle>
            <HeaderSubtitle>Enviada apenas ao reclamante</HeaderSubtitle>
          </HeaderInfo>
        </HeaderLeft>
        <CommentTypeBadge type="PRIVATE_RA" label="Só o consumidor" icon={<User />} />
      </HeaderCard>

      <FieldGroup>
        <Input.TextArea
          value={reservedMessage}
          onChange={(e) => onReservedChange(e.target.value)}
          placeholder={
            reservedRequired
              ? 'Mensagem reservada a quem registrou a reclamação.'
              : 'Mensagem reservada a quem registrou a reclamação (opcional).'
          }
          autoSize={{ minRows: 3, maxRows: 6 }}
          maxLength={RESERVED_MESSAGE_MAX_LENGTH}
          data-testid="ra-reserved-input"
        />
        <CharCounter error={reservedAtLimit}>
          {reservedMessage.length} / {RESERVED_MESSAGE_MAX_LENGTH}
        </CharCounter>
      </FieldGroup>

      <HintBox>
        <Lock />
        <span>Canal reservado — pode detalhar a tratativa com segurança.</span>
      </HintBox>

      <Footer>
        <FooterHint>
          {reservedRequired && !publicRequired
            ? 'Escreva a mensagem reservada ao reclamante.'
            : 'Escreva a resposta pública e confirme a publicação.'}
        </FooterHint>
        <Button
          rounded
          onClick={onReview}
          type="button"
          disabled={!canReview}
          data-testid="ra-review-button"
        >
          Revisar
        </Button>
      </Footer>
    </StepBody>
  );
}

export default ReclameAquiStep;
